import { Box } from '@mui/material';
import { forwardRef } from 'react';

import { LanguageRow } from './LanguageRow.js';
import { spacing } from '../theme/tokens.js';

/**
 * FSE LanguagePicker (web) — radiogroup of LanguageRows.
 *
 * Spec: `Documentation/Claude design files/preview/language-picker.html`.
 * First-class on the login surface: renders each option as a selectable
 * LanguageRow (native script first), wired into a single `role="radiogroup"`.
 * Selection is fully controlled via `value` (the chosen `code`) + `onChange`.
 *
 * Composes `LanguageRow`. Owns the public prop surface — consumers never touch
 * MUI's `sx`. Exposed through @fse/ui so consumers never import @mui/material
 * directly (swap-promise).
 */
export interface LanguageOption {
  /** Stable language code reported through `onChange` (e.g. "xh", "en"). */
  code: string;
  /** Native rendering of the language name (e.g. "isiXhosa"). */
  native: string;
  /** English name shown as subtitle. */
  english: string;
}

export interface LanguagePickerProps {
  options: LanguageOption[];
  /** Currently selected language `code`. */
  value: string;
  /** Fired with the newly selected language `code`. */
  onChange: (code: string) => void;
  /** Accessible label for the radiogroup (e.g. "Choose your language"). */
  'aria-label'?: string;
}

export const LanguagePicker = forwardRef<HTMLDivElement, LanguagePickerProps>(
  function LanguagePicker({ options, value, onChange, ...rest }, ref) {
    return (
      <Box
        ref={ref}
        role="radiogroup"
        aria-label={rest['aria-label']}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${spacing.s3}px`,
        }}
      >
        {options.map((option) => (
          <LanguageRow
            key={option.code}
            native={option.native}
            english={option.english}
            selected={option.code === value}
            onSelect={() => onChange(option.code)}
          />
        ))}
      </Box>
    );
  },
);
